// This component will contain all the logic and the API calls related to the Excursion card results
import {
  Accordion,
  Button,
  Icon,
  PostBookingCard,
  Text,
  TooltipNew,
} from '@msc-b2c/shared-components';
import { BookingStatusEnum, ErrorManagerCodeEnum } from '@msc-b2c/interfaces/enums';
import {
  ExcursionResultsContainerProps,
  GetShopExcursionMyMSCResponse,
  MyMscExcursion,
  OBSObjI,
  PackageDetails,
} from '@msc-b2c/interfaces/interfaces';
import styles from './CardResultsContainer.module.scss';
import {
  chunkArray,
  getAlreadyBookedPassengerId,
  getCardsPerRowForViewport,
  getDisplayButton,
  getNamesOfAlreadyBookedPassengers,
  getPassengerIdsFromSelectionExcludingPassengerIds,
  getPassengerIdsInCartForItemInCabin,
  getPostBookingCardPrices,
  isItemNotFullyBooked,
  isNoEligiblePassengersForExcursion,
  isNoEligiblePassengersForOBS,
  isPriceTypeNotAllowed,
  isReservationAvailable,
  passengersToRecord,
} from '../utils/PostBookingResultsUtilities';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppDispatch,
  daySelected,
  decodeHtmlEntities,
  defaultPillsVisibility,
  findPageByCode,
  getActBaseImagePath,
  getActPlaceholderImagePath,
  getAvailablePrice,
  getCurrencyCode,
  getDemData,
  getDynamicMediaPath,
  getSelectedDay,
  handleScrollForDEM,
  isLayerMyAreaExcursionOpen,
  managePlurals,
  mscGet,
  mscRemove,
  myAreaShopExcursion,
  replacePlaceholders,
  RootState,
  selectedMyAreaExcursionCard,
  selectedMyAreaOBSCard,
  setErrorManagerCode,
  toggleDisplayOverlay,
  updateCartItems,
} from '@msc-b2c/shared-utils';
import { CannotBookPopUp } from '../components/drawercontent-subcomponents/CannotBookPopUp';
import ImageTitle from '../components/drawercontent-subcomponents/ImageTitle';
import Overview from '../components/drawercontent-subcomponents/Overview';
import Passengers from '../components/drawercontent-subcomponents/Passengers';
import { useDispatch, useSelector } from 'react-redux';
import { filterPills } from '../../utils/pillManagementUtils';
import {
  buildExcursionShopRequestKey,
  getCabinIdOfTheLoggedUser,
  getShopExcursionData,
} from '../../utils/myAreaUtils';
import { createRequestGuard } from '../../utils/requestGuardUtils';
import { triggerAddToCartEvent, triggerViewItemEvent } from '../../utils/myAreaAnalytics';
import { extractTooltipContent } from '../utils/TooltipUtilities';
import { Drawer } from '../../drawer/Drawer';
import { addToCart } from '../../utils/myAreaCartUtils';
import { useNavigate } from 'react-router-dom';
import { navigateOrRedirectToRoute } from '../../navigationmenu/utils/navigationMenuUtils';
import AddToCartOverview from '../components/drawercontent-subcomponents/AddToCartOverview';
import { CartItem, PillConfigurations } from '@msc-b2c/interfaces/types';
import { defineItemListName } from '../../utils/analyticsUtils';

function ExcursionResultsContainer({
  postBookingWrapperID,
  ExcursionResultsAEMProps,
  PlanMyCruiseData,
  paginatedCards,
  booking,
  cardPillConfigurations,
  modalTitle,
  modalBodyText,
  modalButtonLabel,
  cardSortingList,
  currentPage,
  setCurrentPage,
  allCards,
  cardsPerResult,
  passengerDetailsSeparator = '',
}: Readonly<ExcursionResultsContainerProps & { cardSortingList: string[] }>) {
  const { buttonLabel } = ExcursionResultsAEMProps;
  const {
    alreadyBookedLabel,
    durationLabel,
    durationValueLabel,
    codeLabel,
    dayLabel,
    dayAtSeaLabelValue,
    explorerPackageLabel,
    perAdultLabel,
    perChildLabel,
    perPackageLabel,
    myPlanButtonLabel,
    myPlanAlreadyPurchasedButtonLabel,
    promotionPriceEnabled,
    showSinglePromotionPrice,
  } = PlanMyCruiseData;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const myAreaExcursionCardData = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaExcursionCard
  );
  const myAreaOBSCardData = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaOBSCard
  );
  const isLayerExcursionOpen = useSelector(
    (state: RootState) => state.myAreaDrawers.isLayerMyAreaExcursionOpen
  );
  const categoryOrder = useSelector((state: RootState) => state.myAreaCommonConfig.categoryOrder);
  const drawerPrice = useSelector((state: RootState) => state.myAreaDrawers.drawerPrice);
  const cabinDetails = useSelector((state: RootState) => state.myCruise.cabinDetails);
  const membershipData = useSelector((state: RootState) => state.myCruise.membershipData);
  const drawerAEMConfig = useSelector((state: RootState) => state.myAreaCommonConfig.drawerProps);
  const drawerPillConfigurations = useSelector(
    (state: RootState) => state.myAreaCommonConfig.drawerProps.drawerPillConfigurations
  );
  const { items: cartItems } = useSelector((state: RootState) => state.myAreaCart);
  const confirmationDrawerProps = useSelector(
    (state: RootState) => state.myAreaCommonConfig.confirmationDrawerProps
  );
  const drawerExcursionAEMConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig?.drawerExcursionProps?.drawerExcursionType
  );
  const myAreaPillsVisibilityConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig.pillsVisibility[postBookingWrapperID]
  );
  const prePaidCardProps = useSelector(
    (state: RootState) => state.myAreaCommonConfig.prePaidCardProps
  );
  const { shopExcursion, shopOBS, excursions, selectedDayFilter } = useSelector(
    (state: RootState) => state.myCruise
  );
  const myAreaPillsVisibility = myAreaPillsVisibilityConfig ?? defaultPillsVisibility;
  const [cardsPerRow, setCardsPerRow] = useState<number>(
    getCardsPerRowForViewport(window.innerWidth)
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isDrawerProcessing, setIsDrawerProcessing] = useState<boolean>(false);
  const [isOptModalOpen, setIsOptModalOpen] = useState<boolean>(false);
  const bannerRef = useRef<HTMLDivElement>();
  const blueBannerElement = useRef<HTMLElement | null>(null);
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const [triggerAddToCart, setTriggerAddToCart] = useState<boolean>(false);
  const [passengersToAdd, setPassengersToAdd] = useState<Record<
    string,
    Record<string, boolean>
  > | null>(null);
  const [selectedLanguages, setSelectedLanguages] = useState<Record<string, string>>({});
  const [isConfirmationDrawer, setIsConfirmationDrawer] = useState(false);
  const [allPassengersInCart, setAllPassengersInCart] = useState(false);
  // Guards duplicate opens and parallel taps for the same excursion card.
  const excursionRequestGuardRef = useRef(createRequestGuard<GetShopExcursionMyMSCResponse>());
  const languageList = myAreaExcursionCardData?.activityDetails?.guideLanguageCodes ?? [];

  const cardResultContainerRef = useRef<HTMLDivElement>(null);

  const [demData, setDemData] = useState<{ type: string; code: string; page: number } | null>(null);

  const activeCabinId = useMemo(() => {
    return getCabinIdOfTheLoggedUser(booking);
  }, [booking]);

  const handleToggle = (index: string) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const defineSelectedLanguages = (card: MyMscExcursion) => {
    if (!cartItems.length) {
      return;
    }

    const relatedCartItem = cartItems.find(
      (item) => item.code === card.code && item.startDate === card.startDate
    );
    const cartLanguages = passengersToRecord(relatedCartItem?.passengers ?? []);
    setSelectedLanguages(cartLanguages);
  };

  const handleCardClick = (
    card: MyMscExcursion,
    filteredPills: PillConfigurations[],
    cardIndex: number
  ) => {
    if (booking.status.code === BookingStatusEnum.OPTIONAL) {
      setIsOptModalOpen(true);
      document.body.classList.add('-overlay');
      return;
    }
    defineSelectedLanguages(card);
    setAllPassengersInCart(false);
    setIsDrawerOpen(true);
    dispatch(isLayerMyAreaExcursionOpen(true));
    dispatch(selectedMyAreaOBSCard(null));
    dispatch(selectedMyAreaExcursionCard(card));
    handleShopRequest(card, cardIndex, { promoBadges: filteredPills });
  };

  useEffect(() => {
    if (passengersToAdd && triggerAddToCart && myAreaExcursionCardData) {
      const promoBadges = (
        myAreaExcursionCardData
          ? analyticsPillsMap[buildExcursionShopRequestKey(myAreaExcursionCardData)]
          : []
      )
        .map((badge) => badge.pillLabel?.toLowerCase().trim() ?? '')
        .join(':');
      const updatedCart: CartItem[] = addToCart(
        myAreaExcursionCardData,
        passengersToAdd,
        undefined,
        {
          selectedLanguages,
          promoBadges,
          itemListName: defineItemListName(myAreaExcursionCardData),
        }
      );
      dispatch(updateCartItems(updatedCart));
      setTriggerAddToCart(false);
      if (confirmationDrawerProps?.showConfirmationDrawer) {
        setIsConfirmationDrawer(true);
      } else {
        resetDrawerState();
      }
    }
  }, [passengersToAdd, triggerAddToCart]);

  const renderTooltip = (longDescription?: string): React.JSX.Element | null => {
    const tooltipContent = extractTooltipContent(longDescription);
    if (!tooltipContent) {
      return null;
    }

    return (
      <div className="additional-notes-container">
        <Text
          text={drawerAEMConfig.tooltipLabel}
          styleOption={'body-default'}
          tagHtml={'span'}
        />
        <TooltipNew
          appliedCssClassNames="default-text"
          triggerAriaLabel={drawerAEMConfig.tooltipLabel}
          content={
            <Text
              text={tooltipContent}
              styleOption={'body-xsmall'}
            />
          }
          positionStyle="top"
        >
          <div>
            <Icon
              icon={drawerAEMConfig.tooltipIcon}
              ariaHidden={true}
            />
          </div>
        </TooltipNew>
      </div>
    );
  };

  const getDrawerContent = (
    order: (
      | 'imageTitle'
      | 'passengers'
      | 'overview'
      | 'arrayOfExcursions'
      | 'additionalNotesTooltip'
    )[]
  ): (React.JSX.Element | null)[] => {
    const components = {
      arrayOfExcursions: (myAreaExcursionCardData?.isPackage && (
        <div className={'excursion-accordions-container'}>
          {myAreaExcursionCardData?.packageDetails?.map(
            (excursionItem: PackageDetails, index: number) => (
              //return an <Accordion /> component for each excursion
              <Accordion
                key={`excursion-${excursionItem.code}-${index}`}
                question={excursionItem?.description ?? ''}
                answer={''}
                closingIcon={'up'}
                isOpen={openIndex === `excursion-${index}`}
                onToggle={() => handleToggle(`excursion-${index}`)}
                openingIcon={'down'}
                autoClose={true}
                multi={true}
              >
                <>
                  <Overview
                    key={`excursion-overview-${excursionItem.code}-${index}`}
                    excursion={excursionItem}
                  />
                  <Text
                    key={`excursion-${excursionItem.code}-${index}long-description`}
                    text={decodeHtmlEntities(excursionItem?.longDescription ?? '')}
                    tagHtml={'p'}
                    styleOption={'body-default'}
                  />
                </>
              </Accordion>
            )
          )}
        </div>
      )) || <></>,
      passengers: (
        <Passengers
          isDrawerOpen={isDrawerOpen}
          setPassengersToAdd={setPassengersToAdd}
          selectedLanguages={selectedLanguages}
          setSelectedLanguages={setSelectedLanguages}
          hidePassengersInCart
          onAllPassengersInCartChange={setAllPassengersInCart}
          isNoEligible={
            isLayerExcursionOpen
              ? isNoEligiblePassengersForExcursion(
                  myAreaExcursionCardData,
                  shopExcursion,
                  activeCabinId,
                  booking
                )
              : isNoEligiblePassengersForOBS(myAreaOBSCardData, shopOBS, activeCabinId, booking)
          }
        />
      ),
      overview: <Overview excursion={myAreaExcursionCardData} />,
      imageTitle: (
        <ImageTitle
          postBookingWrapperID={postBookingWrapperID}
          imageCode={myAreaExcursionCardData?.code ?? ''}
          booking={booking}
        />
      ),
      additionalNotesTooltip: renderTooltip(
        myAreaExcursionCardData?.longDescription ?? myAreaOBSCardData?.longDescription
      ),
    };

    return order.map((key) => components[key]);
  };

  const checkForDEM = useCallback(() => {
    if (!demData) return;

    const { code, type } = demData;

    if (!code && !type) return;

    if (code) {
      const selectedDay = getSelectedDay((allCards as MyMscExcursion[]) ?? [], code);

      if (!selectedDay) {
        handleScrollForDEM(demData);
        dispatch(setErrorManagerCode(ErrorManagerCodeEnum.ITEM_NOT_FOUND));
        setDemData(null);
        return;
      }

      if (selectedDayFilter?.date !== selectedDay.date) {
        dispatch(daySelected(selectedDay));
        return;
      }
    }

    const targetPage = findPageByCode(code, allCards, cardsPerResult);

    if (targetPage !== currentPage) {
      setCurrentPage(targetPage);
      return;
    }

    const card = (indexedCardGroups ?? [])
      .flatMap((group: (OBSObjI | MyMscExcursion)[]) => group)
      .find((c: OBSObjI | MyMscExcursion) => code === c.code);

    setTimeout(() => {
      handleScrollForDEM(demData);
    }, 1);

    if (!card) {
      if (!!code) {
        dispatch(setErrorManagerCode(ErrorManagerCodeEnum.ITEM_NOT_FOUND));
      }
      setDemData(null);
      return;
    }

    if (!isReservationAvailable(card, true, booking, activeCabinId)) {
      setDemData(null);
      return;
    }

    const passengers = getAlreadyBookedPassengerId(card, booking, true, true);
    const isDrawerEnabled = getDisplayButton(passengers, booking, activeCabinId);

    if (!isDrawerEnabled) {
      setDemData(null);
      return;
    }

    const analyticsPills =
      analyticsPillsMap[buildExcursionShopRequestKey(card as MyMscExcursion)] ?? [];
    handleCardClick(card as MyMscExcursion, analyticsPills, card.index ?? 0);

    setDemData(null);
  }, [
    demData,
    selectedDayFilter,
    currentPage,
    excursions,
    allCards,
    cardsPerResult,
    booking,
    activeCabinId,
  ]);

  useEffect(() => {
    if (!demData) return;

    checkForDEM();
  }, [demData, checkForDEM]);

  useEffect(() => {
    if (!!mscGet<string>('session', 'demLogin')) {
      setDemData(getDemData(allCards, cardsPerResult));
      mscRemove('session', 'demLogin');
    }
    const updateLayout = () => {
      setCardsPerRow(getCardsPerRowForViewport(window.innerWidth));
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);

    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  const filteredPillsMap = useMemo<Record<string, PillConfigurations[]>>(() => {
    const map: Record<string, PillConfigurations[]> = {};
    paginatedCards?.forEach((card) => {
      if (!('startDate' in card)) {
        return;
      }

      const cardKey = buildExcursionShopRequestKey(card);
      if (cardKey) {
        map[cardKey] = filterPills(
          card,
          cardPillConfigurations,
          myAreaPillsVisibility,
          categoryOrder,
          booking,
          cartItems
        );
      }
    });
    return map;
  }, [
    paginatedCards,
    cardPillConfigurations,
    myAreaPillsVisibility,
    categoryOrder,
    booking,
    cartItems,
  ]);

  const analyticsPillsMap = useMemo<Record<string, PillConfigurations[]>>(() => {
    const map: Record<string, PillConfigurations[]> = {};
    paginatedCards?.forEach((card) => {
      if (!('startDate' in card)) {
        return;
      }

      const cardKey = buildExcursionShopRequestKey(card);
      if (cardKey) {
        map[cardKey] = filterPills(
          card,
          drawerPillConfigurations,
          myAreaPillsVisibility,
          categoryOrder,
          booking,
          cartItems
        );
      }
    });
    return map;
  }, [
    paginatedCards,
    drawerPillConfigurations,
    myAreaPillsVisibility,
    categoryOrder,
    booking,
    cartItems,
  ]);

  const cardGroups = chunkArray(paginatedCards, cardsPerRow);

  useEffect(() => {
    if (cardResultContainerRef.current) {
      const closestPrepaidContainer = (cardResultContainerRef.current as HTMLDivElement).closest(
        '.prepaidcardcontainer'
      );
      blueBannerElement.current ??= closestPrepaidContainer?.querySelector(
        '.cmp-prepaidcardcontainer .cardWrapper .experience-fragment'
      ) as HTMLElement;

      if (blueBannerElement.current && bannerRef.current) {
        if ('innerHTML' in bannerRef.current) {
          bannerRef.current.innerHTML = '';
        }
        if ('appendChild' in bannerRef.current) {
          bannerRef.current.appendChild(blueBannerElement.current);
        }
      }
    }
  }, [paginatedCards]);

  const activeDrawerCard = isLayerExcursionOpen ? myAreaExcursionCardData : myAreaOBSCardData;
  const passengerIdsInCartForActiveCard = useMemo(() => {
    if (!activeDrawerCard || !booking) {
      return new Set<string>();
    }

    return getPassengerIdsInCartForItemInCabin(activeDrawerCard, cartItems, booking, activeCabinId);
  }, [activeDrawerCard, activeCabinId, booking, cartItems]);

  const passengerIdsToAdd = useMemo(
    () =>
      getPassengerIdsFromSelectionExcludingPassengerIds(
        passengersToAdd,
        passengerIdsInCartForActiveCard
      ),
    [passengerIdsInCartForActiveCard, passengersToAdd]
  );

  const canAddToCartInPostBooking =
    isLayerExcursionOpen &&
    activeDrawerCard &&
    passengerIdsToAdd.length > 0 &&
    (languageList.length <= 1 ||
      passengerIdsToAdd.every((passengerId) => {
        const lang = selectedLanguages[passengerId];
        return typeof lang === 'string' && lang.trim() !== '';
      }));

  const handleShopRequest = (
    card: MyMscExcursion,
    cardIndex: number | undefined,
    extraData?: {
      promoBadges?: PillConfigurations[];
    }
  ) => {
    const requestKey = buildExcursionShopRequestKey(card);

    excursionRequestGuardRef.current.run(
      requestKey,
      () => getShopExcursionData(card),
      (res) => {
        dispatch(myAreaShopExcursion(res as never));
        triggerViewItemEvent(
          { ...card, index: cardIndex },
          booking,
          { ...extraData, shopResponse: res },
          cabinDetails,
          membershipData
        );
      },
      () => {
        resetDrawerState();
        dispatch(setErrorManagerCode(ErrorManagerCodeEnum.NO_EXCURSION_MATCH));
      }
    );
  };

  let durationLabelValueToOverride = '';
  let bookedPassengers: string[] = [];
  let bookedPassengersAllCabins: string[] = [];

  let globalIndex = 1;
  const indexedCardGroups = cardGroups?.map((cards: MyMscExcursion[]) =>
    cards.map((card) => {
      return {
        ...card,
        index: globalIndex++,
      };
    })
  );

  const resetDrawerState = () => {
    // Reset only visual drawer state; request guard is intentionally preserved
    // so reopening the same item does not produce another INT135 call.
    setIsDrawerOpen(false);
    setIsDrawerProcessing(false);
    setIsConfirmationDrawer(false);
    setPassengersToAdd(null);
    toggleDisplayOverlay(false);
    setSelectedLanguages({});
    setAllPassengersInCart(false);
    dispatch(isLayerMyAreaExcursionOpen(false));
    dispatch(selectedMyAreaExcursionCard(null));
    dispatch(selectedMyAreaOBSCard(null));
  };

  return (
    <div
      className={styles.cardResultsContainer}
      ref={cardResultContainerRef}
    >
      {indexedCardGroups?.map((cardGroup: MyMscExcursion[], index: number) => (
        <div
          key={`excursions-${cardGroup?.[index]?.code}-${index}`}
          className={
            index === (indexedCardGroups?.length ?? 0) - 1
              ? `${styles.cardResultsRow} ${styles.cardResultsRowLast}`
              : styles.cardResultsRow
          }
        >
          {cardGroup?.map((card: MyMscExcursion, index: number) => {
            bookedPassengers = getAlreadyBookedPassengerId(card, booking, true);
            bookedPassengersAllCabins = getAlreadyBookedPassengerId(card, booking, true, true);
            durationLabelValueToOverride =
              card.duration > 1
                ? managePlurals(2, durationValueLabel)
                : managePlurals(1, durationValueLabel);
            durationLabelValueToOverride = replacePlaceholders(durationLabelValueToOverride, {
              duration: card.duration.toString(),
            });
            const filteredPills = filteredPillsMap[buildExcursionShopRequestKey(card)] ?? [];
            const analyticsPills = analyticsPillsMap[buildExcursionShopRequestKey(card)] ?? [];
            const cardIndex = (currentPage - 1) * cardsPerResult + (card.index ?? 0);

            return (
              Number(card.inventory?.numberAvailable) > 0 && (
                <PostBookingCard
                  key={card?.id ?? index}
                  id={card?.code ?? index}
                  image={
                    getDynamicMediaPath(card.code) || `${getActBaseImagePath()}/${card.code}.jpg`
                  }
                  title={card?.name ?? card.description}
                  imageCode={card?.code ?? ''}
                  imageType="act"
                  placeholderImagePath={getActPlaceholderImagePath()}
                  displayedPills={(() => {
                    // Sort pills by cardSortingList priority
                    return Array.isArray(filteredPills)
                      ? [...filteredPills].sort((a, b) => {
                          const aIdx = cardSortingList.indexOf(a.pillType ?? '');
                          const bIdx = cardSortingList.indexOf(b.pillType ?? '');
                          if (aIdx === -1 && bIdx === -1) return 0;
                          if (aIdx === -1) return 1;
                          if (bIdx === -1) return -1;
                          return aIdx - bIdx;
                        })
                      : filteredPills;
                  })()}
                  onClick={() => handleCardClick(card, analyticsPills, cardIndex)}
                  clickDisabled={
                    !isPriceTypeNotAllowed(booking) &&
                    (!isReservationAvailable(card, true, booking, activeCabinId) ||
                      !getDisplayButton(bookedPassengersAllCabins, booking, activeCabinId))
                  }
                >
                  <PostBookingCard.PreTitle
                    text={`${dayLabel}`}
                    day={card.day}
                    portCode={card.portCode}
                    startDate={card.startDate}
                    dayAtSeaLabelValue={dayAtSeaLabelValue}
                    explorerPackageLabel={card.isPackage ? explorerPackageLabel : ''}
                  />
                  <PostBookingCard.Title packageTitle={card?.name ?? card.description} />
                  <PostBookingCard.Description
                    descriptionItems={[
                      ...(!card.isPackage && card.duration > 0
                        ? [
                            {
                              descriptionLabel: durationLabel,
                              descriptionValue: durationLabelValueToOverride,
                            },
                          ]
                        : []),
                      {
                        descriptionLabel: codeLabel,
                        descriptionValue: card.code,
                      },
                    ]}
                  />
                  {alreadyBookedLabel && bookedPassengers.length > 0 && (
                    <PostBookingCard.Description
                      descriptionItems={[
                        {
                          descriptionLabel: alreadyBookedLabel,
                          descriptionValue: passengerDetailsSeparator
                            ? getNamesOfAlreadyBookedPassengers(bookedPassengers, booking).join(
                                passengerDetailsSeparator
                              )
                            : getNamesOfAlreadyBookedPassengers(bookedPassengers, booking),
                        },
                      ]}
                    />
                  )}
                  {isPriceTypeNotAllowed(booking) ? (
                    <Button
                      text={prePaidCardProps.myPlanButtonPurchaseDisabledLabel}
                      styleOption={'cmp-button__secondary purchase-disabled'}
                    />
                  ) : (
                    <PostBookingCard.PriceSection
                      prices={
                        isItemNotFullyBooked(bookedPassengersAllCabins, booking, activeCabinId)
                          ? getPostBookingCardPrices({
                              card,
                              promotionPriceEnabled,
                              showSinglePromotionPrice,
                              perCabinLabel: perPackageLabel,
                              perAdultLabel,
                              perChildLabel,
                            })
                          : []
                      }
                      buttonLabel={
                        getDisplayButton(bookedPassengersAllCabins, booking, activeCabinId) &&
                        bookedPassengers.length > 0
                          ? myPlanAlreadyPurchasedButtonLabel || buttonLabel || myPlanButtonLabel
                          : buttonLabel || myPlanButtonLabel
                      }
                      displayButton={getDisplayButton(
                        bookedPassengersAllCabins,
                        booking,
                        activeCabinId
                      )}
                    />
                  )}
                </PostBookingCard>
              )
            );
          })}
          {index === 0 && (
            <div className={styles.blueBannerContainer}>
              <div ref={bannerRef as React.RefObject<HTMLDivElement>}></div>
            </div>
          )}
        </div>
      ))}
      {!isConfirmationDrawer && (
        <Drawer
          postBookingWrapperID={postBookingWrapperID}
          closeIcon={drawerAEMConfig.closeDrawerIcon}
          firstButtonLabel={drawerExcursionAEMConfig?.addToCartButtonLabel}
          limitedSeatsPlaceholder={drawerAEMConfig.limitedSeatsPlaceholder}
          secondButtonLabel=""
          alreadyInCartMessageLabel={
            allPassengersInCart ? drawerAEMConfig.alreadyInCartMessageLabel : ''
          }
          alreadyInCartCtaLabel={allPassengersInCart ? drawerAEMConfig.alreadyInCartCtaLabel : ''}
          alreadyInCartCtaLink={
            allPassengersInCart ? drawerAEMConfig.alreadyInCartCtaLink : undefined
          }
          processingFirstButtonLabel={drawerExcursionAEMConfig?.addingToCartButtonLabel}
          processingFirstButtonIcon={drawerExcursionAEMConfig?.addingToCartButtonIcon}
          isDrawerOpen={isDrawerOpen}
          enableClosingByClickingOutside={true}
          onClose={resetDrawerState}
          onApply={() => {
            toggleDisplayOverlay(false);
            setTriggerAddToCart(true);
            setIsDrawerProcessing(true);
            const activeCard = isLayerExcursionOpen ? myAreaExcursionCardData : myAreaOBSCardData;
            triggerAddToCartEvent(
              activeCard,
              booking,
              cabinDetails,
              getCurrencyCode(),
              getAvailablePrice(drawerPrice),
              {
                promoBadges:
                  isLayerExcursionOpen && activeCard
                    ? analyticsPillsMap[
                        buildExcursionShopRequestKey(activeCard as MyMscExcursion)
                      ] ?? []
                    : [],
                passengersToAdd,
              },
              membershipData
            );
          }}
          stylingOption="transparent-header"
          isProcessing={isDrawerProcessing}
          disabled={!canAddToCartInPostBooking}
          hiddenFooter={
            isPriceTypeNotAllowed(booking) ||
            isNoEligiblePassengersForExcursion(
              myAreaExcursionCardData,
              shopExcursion,
              activeCabinId,
              booking
            )
          }
        >
          <>
            {getDrawerContent([
              'imageTitle',
              'overview',
              'passengers',
              'arrayOfExcursions',
              ...(!drawerAEMConfig?.disableAdditionalNotesTooltip
                ? (['additionalNotesTooltip'] as const)
                : []),
            ])}
          </>
        </Drawer>
      )}
      {isConfirmationDrawer && (
        <Drawer
          postBookingWrapperID={postBookingWrapperID}
          title={confirmationDrawerProps?.title}
          titleIcon={confirmationDrawerProps?.titleIcon}
          closeIcon={drawerAEMConfig.closeDrawerIcon}
          firstButtonLabel={confirmationDrawerProps?.continueShoppingButtonLabel}
          secondButtonLabel={confirmationDrawerProps?.viewCartButtonLabel}
          isDrawerOpen={isDrawerOpen}
          enableClosingByClickingOutside={true}
          onClose={resetDrawerState}
          onClear={() => {
            resetDrawerState();
            navigateOrRedirectToRoute(confirmationDrawerProps?.viewCartLink || {}, navigate);
          }}
          onApply={resetDrawerState}
          stylingOption="confirmation-drawer"
          footerButtonsVariant={'secondary'}
        >
          <AddToCartOverview
            postBookingCard={myAreaExcursionCardData}
            passengersToAdd={passengersToAdd}
          />
        </Drawer>
      )}
      {isOptModalOpen && (
        <CannotBookPopUp
          modalTitle={modalTitle}
          modalBodyText={modalBodyText}
          modalButtonLabel={modalButtonLabel}
          handleClose={() => {
            document.body.classList.remove('-overlay');
            setIsOptModalOpen(false);
          }}
          handleClick={() => {
            document.body.classList.remove('-overlay');
            setIsOptModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default ExcursionResultsContainer;
